import { useCallback, useEffect, useState } from 'react';
import { NavLink, Route, Routes, useLocation } from 'react-router-dom';

import { api, getToken, login as doLogin, setToken, type AuthUser } from './lib/api';
import { Admin } from './pages/Admin';
import { Clients, ClientDetail } from './pages/Clients';
import { Containers } from './pages/Containers';
import ConnectIQ from './pages/ConnectIQ';
import { Endpoints } from './pages/Endpoints';
import { Llm } from './pages/Llm';
import { Overview } from './pages/Overview';
import { ServerDetail, Servers } from './pages/Servers';
import { Products } from './pages/Products';

type Theme = 'light' | 'dark';

function useTheme(): [Theme, () => void] {
  const [theme, setThemeState] = useState<Theme>(
    () => (document.documentElement.getAttribute('data-theme') as Theme) ?? 'dark',
  );

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try {
        localStorage.setItem('piq-theme', next);
      } catch {
        // A blocked localStorage costs the preference on reload, not the toggle.
      }
      return next;
    });
  }, []);

  return [theme, toggle];
}

interface AuthConfig {
  ssoEnabled: boolean;
  ssoDomains: string[];
}

/**
 * Picks up a token handed back in the URL fragment by the SSO callback, and
 * clears it from the address bar.
 *
 * The fragment is used rather than a query string because browsers never send it
 * to a server — so the token stays out of access logs, proxy logs and Referer
 * headers. Once read it is replaced immediately, so it does not sit in history or
 * survive a copied URL.
 */
function consumeSsoFragment(): boolean {
  const hash = window.location.hash;
  if (!hash.startsWith('#token=')) return false;
  setToken(decodeURIComponent(hash.slice('#token='.length)));
  window.history.replaceState(null, '', window.location.pathname);
  return true;
}

function Login({ onDone }: { onDone: (u: AuthUser) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(() => {
    // The callback redirects here with a reason when sign-in fails, rather than
    // dumping a stack trace or leaving a blank page.
    const reason = new URLSearchParams(window.location.search).get('sso_error');
    if (reason) window.history.replaceState(null, '', window.location.pathname);
    return reason;
  });

  useEffect(() => {
    api<AuthConfig>('/auth/config')
      .then(setConfig)
      // A failed config fetch just means no Microsoft button; the password form
      // still works, which is the point of keeping local login available.
      .catch(() => setConfig({ ssoEnabled: false, ssoDomains: [] }));
  }, []);

  return (
    <div className="login-page">
      <form
        className="login-card"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            onDone(await doLogin(email, password));
          } catch {
            // The API returns the same message for unknown user, wrong password
            // and inactive account. Repeating it verbatim keeps it that way.
            setError('Invalid email or password');
          } finally {
            setBusy(false);
          }
        }}
      >
        <h1 className="brand">
          Platform<span>IQ</span>
        </h1>
        <p className="login-sub">Estate inventory and monitoring</p>

        {config?.ssoEnabled && (
          <>
            <button
              type="button"
              className="sso-button"
              // A full navigation, not fetch: the Entra authorisation endpoint is
              // a browser redirect flow and cannot be completed with XHR.
              onClick={() => {
                window.location.href = '/api/v1/auth/sso/start';
              }}
            >
              <span className="ms-logo" aria-hidden="true">
                <i /><i /><i /><i />
              </span>
              Sign in with Microsoft
            </button>
            <div className="or">or sign in with a password</div>
          </>
        )}

        <label htmlFor="email">Email</label>
        <input id="email" type="email" autoComplete="username" required value={email}
          onChange={(e) => setEmail(e.target.value)} />

        <label htmlFor="password">Password</label>
        <input id="password" type="password" autoComplete="current-password" required value={password}
          onChange={(e) => setPassword(e.target.value)} />

        {error && <p className="form-error" role="alert">{error}</p>}

        <button type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </div>
  );
}

// The dot carries the same hue the entity wears everywhere else, so the colour
// scheme is taught by the one component present on every page. Overview has no
// dot: it is every entity at once, and a seventh colour would say otherwise.
const NAV: Array<{ to: string; label: string; end?: boolean; dot?: string }> = [
  { to: '/', label: 'Overview', end: true },
  { to: '/clients', label: 'Clients', dot: 'client' },
  { to: '/servers', label: 'Servers', dot: 'server' },
  { to: '/containers', label: 'Containers', dot: 'container' },
  { to: '/products', label: 'Products', dot: 'product' },
  { to: '/endpoints', label: 'Endpoints', dot: 'endpoint' },
  { to: '/llm', label: 'LLM', dot: 'llm' },
  { to: '/connectiq', label: 'ConnectIQ', dot: 'connectiq' },
];

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [theme, toggleTheme] = useTheme();
  const location = useLocation();

  useEffect(() => {
    // Must run before the token check: on return from Entra the only credential
    // we have is sitting in the URL fragment.
    consumeSsoFragment();

    if (!getToken()) {
      setChecking(false);
      return;
    }
    api<AuthUser>('/auth/me')
      .then(setUser)
      .catch(() => setToken(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="boot">Loading…</div>;
  if (!user) return <Login onDone={setUser} />;

  return (
    <div className="shell">
      <header className="topbar">
        <NavLink to="/" className="brand brand-sm">
          Platform<span>IQ</span>
        </NavLink>

        <nav aria-label="Sections">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}>
              {n.dot && <span className={`nav-dot nav-${n.dot}`} aria-hidden="true" />}
              {n.label}
            </NavLink>
          ))}
          {/* Shown only to those who can use it. The API refuses regardless — this
              just keeps a link off the page for everyone it would only 403 for. */}
          {(user.role === 'ADMIN' || user.role === 'SUPERADMIN') && (
            <NavLink to="/admin">Administration</NavLink>
          )}
        </nav>

        <div className="topbar-right">
          <button type="button" className="ghost" onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          <span className="who" title={user.email}>{user.name}</span>
          <button type="button" className="ghost" onClick={() => { setToken(null); setUser(null); }}>
            Sign out
          </button>
        </div>
      </header>

      <main key={location.pathname}>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/servers" element={<Servers />} />
          <Route path="/servers/:id" element={<ServerDetail />} />
          <Route path="/containers" element={<Containers />} />
          <Route path="/products" element={<Products />} />
          <Route path="/endpoints" element={<Endpoints />} />
          <Route path="/llm" element={<Llm />} />
          <Route path="/connectiq" element={<ConnectIQ />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<p className="empty">Not found.</p>} />
        </Routes>
      </main>
    </div>
  );
}
